import dayjs from 'dayjs'

export const EPOCH_DATETIME = '1970-01-01T00:00:00+00:00'
export const FORMAT_DD_MM_YYYY = 'DD/MM/YYYY'
export const FORMAT_MM_DD_YYYY = 'MM/DD/YYYY'
export const FORMAT_YYYY_MM_DD = 'YYYY/MM/DD'
export const ISO_DATE_FORMAT = 'YYYY-MM-DD'
export const ISO_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ'
export const DEFAULT_TIME_FORMAT = 'hh:mm A'
export const DEFAULT_TIMESTAMP_FORMAT = 'MM/DD/YYYY hh:mm:ss A'
export const AG_GRID_FILTER_FORMAT = 'YYYY-MM-DD HH:mm:ss'

/**
 * Formats a date value into a string.
 * @param date Date value to format (`Date | string | null`).
 * @param format Optional dayjs format; defaults to `FORMAT_MM_DD_YYYY`.
 * @param utc Whether to parse/format in UTC mode (defaults to `false`).
 * @returns Formatted date string, or `''` when `date` is null/invalid.
 */
export const formatDate = (
  date: Date | string | null,
  format?: string | null,
  utc: boolean = false,
): string => {
  const d = utc ? dayjs.utc(date) : dayjs(date)
  return d.isValid() ? d.format(format || FORMAT_MM_DD_YYYY) : ''
}

/**
 * Formats a date value as a time string.
 * @param date Date value to format (`Date | string | null`).
 * @param format Optional dayjs format; defaults to `DEFAULT_TIME_FORMAT`.
 * @param utc Whether to parse/format in UTC mode (defaults to `false`).
 * @returns Formatted time string, or `''` when `date` is null/invalid.
 */
export const formatTime = (
  date: Date | string | null,
  format?: string | null,
  utc: boolean = false,
): string => {
  const d = utc ? dayjs.utc(date) : dayjs(date)
  return d.isValid() ? d.format(format || DEFAULT_TIME_FORMAT) : ''
}

/**
 * Formats a date value as a timestamp string.
 * @param date Date value to format (`Date | string | null`).
 * @param utc Whether to parse/format in UTC mode (defaults to `false`).
 * @returns Timestamp string in `DEFAULT_TIMESTAMP_FORMAT`, or `''` when invalid.
 */
export const formatTimestamp = (
  date: Date | string | null,
  utc: boolean = false,
): string => {
  return formatDate(date, DEFAULT_TIMESTAMP_FORMAT, utc)
}

type FormatDateRangeOptions = {
  dateFormat?: string
  utc?: boolean
}

/**
 * Formats a date range string from start and end dates.
 * @param startDate Start date (`Date | string | null`).
 * @param endDate End date (`Date | string | null`).
 * @param options Range options (`dateFormat`, `utc`).
 * @returns `<start> - <end>` when both dates are valid; otherwise `''`.
 */
export const formatDateRange = (
  startDate: Date | string | null,
  endDate: Date | string | null,
  { dateFormat = FORMAT_DD_MM_YYYY, utc = false }: FormatDateRangeOptions,
): string => {
  const start = formatDate(startDate, dateFormat, utc)
  const end = formatDate(endDate, dateFormat, utc)
  return start && end ? `${start} - ${end}` : ''
}

/**
 * Converts a date value to server date format (`YYYY-MM-DD`).
 * @param date Date value to convert (`Date | string | null`).
 * @param utc Whether to parse/format in UTC mode (defaults to `false`).
 * @returns Server-formatted date string, or `''` when `date` is null/invalid.
 */
export const toServerDate = (
  date: Date | string | null,
  utc: boolean = false,
): string => {
  return formatDate(date, ISO_DATE_FORMAT, utc)
}

/**
 * Returns the epoch timestamp in milliseconds for a date value.
 * @param date Date value (`Date | string`), defaults to `EPOCH_DATETIME`.
 * @returns Milliseconds since Unix epoch, or `null` when the date is invalid.
 */
export const getEpochMs = (
  date: Date | string = EPOCH_DATETIME,
): number | null => {
  const d = dayjs(date)
  return d.isValid() ? d.valueOf() : null
}
